'use client'

import { useState } from 'react'
import { useIntl } from 'react-intl'
import {
  CheckmarkShieldOutline16,
  AuthenticityDiamond16,
  Van16,
  ElectronicsBadge16,
  Tag16,
} from '@vinted/monochrome-icons'

import { EscrowFeesModel } from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session'
import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { UserBundleDiscountModel } from '@marketplace-web/bundles/items-bundle-data'

import DetailedItemPriceBreakdownModal from '../../DetailedItemPriceBreakdownModal'
import DiscountBadge from '../DiscountBadge'

type Props = {
  escrowFees: EscrowFeesModel
  isSellerBusiness: boolean
  itemUserId: number
  itemTitle: string
  itemPrice: CurrencyAmountModel
  itemPhotoSrc?: string
  itemId: number
  bundleDiscount: UserBundleDiscountModel | null
}

const DiscountBadges = ({
  escrowFees,
  isSellerBusiness,
  itemUserId,
  itemTitle,
  itemPrice,
  itemPhotoSrc,
  itemId,
  bundleDiscount,
}: Props) => {
  const [isPriceBreakdownModalOpen, setIsPriceBreakdownModalOpen] = useState(false)
  const { track } = useTracking()
  const translate = useTranslate('item.discount_badges')
  const isBundleDiscountAbTest = useAbTest('bundle_discount_badge')
  useTrackAbTest(isBundleDiscountAbTest, bundleDiscount?.isEnabled)
  const showBundleDiscountBadge =
    isBundleDiscountAbTest?.variant === 'on' && bundleDiscount?.isEnabled

  const { user } = useSession()
  const userId = user?.id
  const { locale } = useIntl()

  const userTypeSuffix = isSellerBusiness ? '_pro' : ''
  const { buyerProtection, itemVerification, shipping, electronicsVerification } = escrowFees

  const isItemOwner = userId === itemUserId

  const handleModalOpen = () => {
    setIsPriceBreakdownModalOpen(true)

    track(
      clickEvent({
        screen: Screen.Item,
        target: ClickableElement.DiscountInfo,
        targetDetails: JSON.stringify({
          item_id: itemId,
        }),
      }),
    )
  }

  const formattedItemPrice = formatCurrencyAmount(itemPrice, locale)

  const getBuyerProtectionFeeText = () =>
    buyerProtection?.isFree
      ? translate(`free_buyer_protection_fee${userTypeSuffix}`)
      : translate(`buyer_protection_fee${userTypeSuffix}`, {
          discount: buyerProtection?.discountPercentage,
        })

  const getItemVerificationFeeText = () =>
    itemVerification?.isFree
      ? translate('free_item_verification_fee')
      : translate('item_verification_fee', {
          discount: itemVerification?.discountPercentage,
        })

  const getElectronicsVerificationFeeText = () =>
    electronicsVerification?.isFree
      ? translate('free_electronics_verification_fee')
      : translate('electronics_verification_fee', {
          discount: electronicsVerification?.discountPercentage,
        })

  const getShippingFeeText = () =>
    translate('postage', {
      discount: shipping?.maxDiscountPercentage,
    })

  const getBundleDiscountText = () => {
    if (!bundleDiscount?.isEnabled) return null

    const fractions = bundleDiscount.discounts.map(discount => Number(discount.fraction))
    const biggestFraction = Math.max(...fractions)
    const discount = (biggestFraction * 100).toFixed(0)

    return translate('discount_bundle', { discount })
  }

  const discountBadges = [
    {
      iconType: CheckmarkShieldOutline16,
      isVisible: !!buyerProtection?.discountPercentage,
      content: getBuyerProtectionFeeText(),
      id: 'buyer-protection-fee',
    },
    {
      iconType: AuthenticityDiamond16,
      isVisible: !!itemVerification?.discountPercentage,
      content: getItemVerificationFeeText(),
      id: 'item-verification-fee',
    },
    {
      iconType: ElectronicsBadge16,
      isVisible: !!electronicsVerification?.discountPercentage,
      content: getElectronicsVerificationFeeText(),
      id: 'electronics-verification-fee',
    },
    {
      iconType: Van16,
      isVisible: !!shipping?.maxDiscountPercentage,
      content: getShippingFeeText(),
      id: 'shipping',
    },
    {
      iconType: Tag16,
      isVisible: !!showBundleDiscountBadge,
      content: getBundleDiscountText(),
      id: 'bundle',
    },
  ]

  const itemHasDiscounts = discountBadges.some(({ isVisible }) => isVisible)

  if (!itemHasDiscounts || isItemOwner) return null

  return (
    <div data-testid="discount-badges-container">
      <button
        type="button"
        data-testid="discount-badge-button"
        className="u-flexbox u-flex-wrap u-gap-small u-padding-vertical-medium"
        onClick={handleModalOpen}
      >
        {discountBadges.map(badge => (
          <DiscountBadge key={badge.id} {...badge} />
        ))}
      </button>
      <DetailedItemPriceBreakdownModal
        isVisible={isPriceBreakdownModalOpen}
        onClose={() => setIsPriceBreakdownModalOpen(false)}
        itemTitle={itemTitle}
        itemPrice={formattedItemPrice}
        itemPhotoSrc={itemPhotoSrc}
        escrowFees={escrowFees}
        isSellerBusiness={isSellerBusiness}
        isItemOwner={isItemOwner}
        itemId={itemId}
      />
    </div>
  )
}

export default DiscountBadges
